import type { RouteObject } from "react-router-dom";

import * as $root from "@/pages/root";
// oxlint-disable-next-line no-legacy-ui-layer/no-legacy-ui-layer -- unchanged import, reported only because it moved from Routes.tsx; the route error boundary has no newComponents target
import { ErrorBoundary } from "components/ErrorBoundary";

const pages = import.meta.glob([
  "../../pages/**/*.tsx",
  "!../../pages/root.tsx",
  "!../../pages/**/*.test.tsx",
  "!../../pages/**/*.spec.tsx",
]);

type Route = {
  index?: boolean;
  path?: string;
  $pathless?: string;
  lazy?: () => Promise<unknown>;
  children?: Route[];
  errorElement?: JSX.Element;
};

/**
 * React Router (7.18) keeps a route whose `lazy()` rejected marked as still loading and renders a
 * blank page instead of its error element when a page chunk fails to download. Resolving to a
 * loader that rethrows the import failure hands it to the route's error element.
 */
export const lazyRouteModule =
  <T,>(load: () => Promise<T>) =>
  () =>
    load().catch((error: unknown) => ({
      loader: () => {
        throw error;
      },
    }));

/**
 * The app's route tree, built from the files under `src/pages`. Each call returns a new tree,
 * so the app's router and a test's router never share route objects.
 */
export const buildRoutes = (): RouteObject[] => {
  const root: Route = Object.assign({ path: "/", errorElement: <ErrorBoundary /> }, $root);

  for (const path of Object.keys(pages)) {
    const filename = path.match(/\.\/pages\/(.*)\.tsx$/)?.[1];
    if (!filename) continue;

    const segments = filename.split("/").map((s) => s.replaceAll(".", "/"));

    let route = root;
    for (const segment of segments) {
      let child = route.children?.find(
        (r) =>
          r.path === segment || r.path === segment.replace("$", ":") || r.$pathless === segment,
      );

      if (!child) {
        child = { errorElement: <ErrorBoundary /> };
        if (segment === "index") {
          child.index = true;
        } else if (segment.startsWith("__")) {
          child.$pathless = segment;
        } else {
          child.path = segment.replaceAll(/\$(\w+)/g, ":$1").replaceAll("$", "*");
        }
        route.children ||= [];
        route.children.push(child);
      }
      route = child;
    }

    route.lazy = lazyRouteModule(pages[path]);
  }

  return [root as RouteObject];
};
